import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TitleMask from '@/components/Common/Layout/TitleMask';
import Button, {
  ButtonSize,
  ButtonRadius,
  ButtonVariant,
} from '@/components/Common/Button';
import SelectBox from '@/components/Common/SelectBox';
import Label from '@/components/Common/Label/Label';
import Input from '@/components/Common/Input';
import AttachFile from '@/components/Common/AttachFile';
import { InquiryType } from '@/api/inquiries';
import useInquiryCreate from '@/hooks/cs/useInquiryCreate';
import { ERROR_MESSAGE } from '@/utils/validation';

const Editor = dynamic(() => import('@/components/Common/Editor/Editor'), {
  ssr: false,
});

const CreateLayout = styled.article`
  width: 800px;
  margin: 0 auto;
`;
const FormGroup = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #ededed;

  &:last-child {
    border-bottom: 0;
  }

  label {
    width: 126px;
    color: ${({ theme }) => theme.palette.gray};
    font-size: 13px;
    font-weight: 500;
    line-height: 19px;
  }

  select {
    margin: 0;
    padding: 8px 0px 8px 19px;
    height: 40px;
    box-sizing: border-box;
    border-bottom: 2px solid #e9ecef;
    color: #212529;
  }

  input {
    padding: 8px 0px 8px 19px;
    height: 40px;
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.palette.paleGray};
    border-radius: 8px;
  }
`;
const TextareaFormGroup = styled(FormGroup)`
  flex-direction: column;
  align-items: normal;
  margin-top: 18px;
  padding: 0 0 18px 0;
`;

const ButtonBox = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  button {
    margin: 48px 8px 0;
  }
`;
const FormEditorLayout = styled.div`
  margin-top: 8px;
`;

export default function Create() {
  const router = useRouter();
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(AdInquirySchema),
  });
  const [selectedOption, setSelectedOption] = useState('');
  const createMutate = useInquiryCreate();

  const onSubmit = (data: any) => {
    if (createMutate.isLoading) {
      return;
    }
    const { name, email, contact, title, contents } = data;
    const type = InquiryType.Ads;

    createMutate.mutate(
      {
        type,
        name,
        email,
        contact,
        title,
        contents,
      },
      {
        onSuccess: res => {
          alert(res.message);
          router.push('/cs/inquiries');
        },
        onError: () => {
          alert('오류');
        },
      }
    );
  };

  return (
    <TitleMask title="광고 문의하기">
      <CreateLayout>
        <form
          onSubmit={handleSubmit(onSubmit, data => {
            const { message } = Object.values(data)[0];
            window.alert(message);
          })}
        >
          {/**
                     <FormGroup>
                     <Label id="adSelectLabel" isRequired>
                     <span>문의유형</span>
                     </Label>
                     <SelectBox
                     selectedValue={selectedOption}
                     options={AD_OPTIONS}
                     onChange={e => setSelectedOption(e.target.value)}
                     />
                     </FormGroup>
                     */}
          {Fields.map(field => (
            <Controller
              control={control}
              name={field.name}
              render={({ field: { onChange, value } }) => (
                <FormGroup>
                  <Label id={field.name} isRequired>
                    <span>{field.label}</span>
                  </Label>
                  <Input
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={onChange}
                    autoComplete="off"
                  />
                </FormGroup>
              )}
            />
          ))}
          <Controller
            name="contents"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextareaFormGroup>
                <Label id="contents" isRequired>
                  내용
                </Label>
                <FormEditorLayout>
                  <Editor name="contents" value={value} onChange={onChange} />
                </FormEditorLayout>
              </TextareaFormGroup>
            )}
          />
          {/**
                     <FormGroup>
                     <Label id="attachFile" isRequired>
                     첨부파일
                     </Label>
                     <AttachFile isError />
                     </FormGroup>
                     */}
          <ButtonBox>
            <Button
              type="button"
              width={250}
              height={56}
              size={ButtonSize.Big}
              radius={ButtonRadius.Oval}
              variant={ButtonVariant.White}
              label="취소"
              onClick={() => {
                router.push('/');
              }}
            />
            <Button
              type="submit"
              width={250}
              height={56}
              size={ButtonSize.Big}
              radius={ButtonRadius.Oval}
              variant={ButtonVariant.Default}
              label="등록"
            />
          </ButtonBox>
        </form>
      </CreateLayout>
    </TitleMask>
  );
}

const AD_OPTIONS = [
  {
    value: 'value1',
    text: '광고문의',
  },
  {
    value: 'value2',
    text: '이용방법',
  },
  {
    value: 'value3',
    text: '제휴문의',
  },
  {
    value: 'value4',
    text: '기타',
  },
];

const AdInquirySchema = yup.object({
  name: yup
    .string()
    .required(ERROR_MESSAGE.CS.NAME.REQUIRED)
    .max(100, ERROR_MESSAGE.CS.NAME.MAX),
  email: yup
    .string()
    .email('올바른 이메일을 입력해주세요. ')
    .required('이메일을 입력해주세요.'),
  contact: yup
    .string()
    .matches(
      /^01([0|1|6|7|8|9])-([0-9]{3,4})-([0-9]{4})$/,
      "'-'를 포함하여 올바른 휴대폰 번호를 입력해주세요."
    )
    .required('휴대폰 번호를 입력해주세요.'),
  title: yup
    .string()
    .required('제목을 입력해주세요.')
    .max(100, ERROR_MESSAGE.CS.TITLE.MAX),
  contents: yup.string().required('내용을 입력해주세요.'),
});

const Fields = [
  {
    label: '이름',
    name: 'name',
    type: 'string',
    placeholder: '이름을 입력해 주세요.',
    required: true,
  },
  {
    label: '이메일',
    name: 'email',
    type: 'string',
    placeholder: '이메일을 입력해 주세요.',
    required: true,
  },
  {
    label: '연락처',
    name: 'contact',
    type: 'contact',
    placeholder: "'-'를 포함하여 휴대폰 번호를 입력해 주세요.",
    required: true,
  },
  /**
     {
    label: '확인요청 URL',
    name: 'url',
    type: 'url',
    placeholder: 'http://URL 형식',
    required: true,
  }, */
  {
    label: '제목',
    name: 'title',
    type: 'string',
    placeholder: '제목을 입력해 주세요.',
    required: true,
  },
];
